import React, { useRef } from 'react';
import useClickOutside from '../../hooks/useClickOutside';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Trash2, XCircle } from 'lucide-react';

interface TaskActionsPopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  onDrop: () => void;
  onDelete: () => void;
  anchorRef: React.RefObject<HTMLElement>;
}

/**
 * TaskActionsPopover - 桌面版彈出式操作選單
 *
 * 設計理念：
 * - macOS 風格的 Popover 選單
 * - 從 ⋯ 按鈕錨點彈出
 * - 點擊外部自動關閉
 * - 使用淡入/縮放動畫
 */
export const TaskActionsPopover: React.FC<TaskActionsPopoverProps> = ({
  isOpen,
  onClose,
  onComplete,
  onDrop,
  onDelete,
  anchorRef,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);

  useClickOutside([popoverRef, anchorRef], onClose, isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, scale: 0.95, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -8 }}
          transition={{ duration: 0.15 }}
          className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-modal-nested"
        >
          {/* Complete 選項 */}
          <button
            onClick={() => {
              onComplete();
              onClose();
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-green-50 active:bg-green-100 transition-colors"
          >
            <Check className="w-4 h-4 text-green-600" />
            <div className="flex-1">
              <div className="text-sm font-medium text-green-600">Complete</div>
            </div>
          </button>

          {/* 分隔線 */}
          <div className="border-t border-gray-100" />

          {/* Drop 選項（需確認操作） */}
          <button
            onClick={() => {
              onClose(); // 先關閉 Popover
              onDrop(); // 觸發確認對話框
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-orange-50 active:bg-orange-100 transition-colors"
          >
            <XCircle className="w-4 h-4 text-orange-600" />
            <div className="flex-1">
              <div className="text-sm font-medium text-orange-600">Drop</div>
            </div>
          </button>

          {/* 分隔線 */}
          <div className="border-t border-gray-100" />

          {/* Delete 選項（永久刪除） */}
          <button
            onClick={() => {
              onClose(); // 先關閉 Popover
              onDelete(); // 觸發確認對話框
            }}
            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50 active:bg-red-100 transition-colors"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
            <div className="flex-1">
              <div className="text-sm font-medium text-red-600">Delete</div>
            </div>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
